/* ═══ PRO BONO BILLING (2026-09-11) ═══
   Ivin: "A pro bono job. This would give us the opportunity to ingest
   professionals, but if we don't want to charge them there is a mechanism for
   that." First case: the NYU class visit (Winslow Porter, Sept 15 and 16).

   The mechanism is ONE field, `billing`, on quotes and jobs:
     "standard"  the default; an ABSENT field means standard, so no migration
     "pro_bono"  nothing is charged
   and ONE quote-level discount named "Pro bono" at 100%. Line rates are never
   zeroed: the PDF still prints every line at its rate, the subtotal, the Pro
   bono line, and Total $0, so the value given stays visible.

   Pure module, no imports: fixlib.mjs (node) reads BILLING_VALUES from here,
   builderHelpers reads isProBono, and neither may pull React or Firestore in.
   Everything that decides "is this pro bono" or "what does the toggle write"
   goes through these helpers so the builder, the job card, the AI refine step
   and the CRM tools can never disagree. */

export const BILLING_VALUES = ["standard", "pro_bono"];
export const PRO_BONO_DISCOUNT_NAME = "Pro bono";
export const PRO_BONO_TAG = "pro_bono";
/* Client-facing. No em dashes. */
export const PRO_BONO_PRINT_NOTE = "Provided pro bono by New York Capture.";

/** Any stored value collapses to one of the two allowed values. */
export const normalizeBilling = (v) => (v === "pro_bono" ? "pro_bono" : "standard");

/** True only for the exact stored value; absent, null, or anything else is standard. */
export const isProBono = (doc) => doc?.billing === "pro_bono";

/** The Pro bono discount is found by NAME (trimmed, case-insensitive), because
 *  the discount stays user-editable like any other and its key is not stable
 *  across an AI refine reply, which re-keys every discount it returns. */
export const isProBonoDiscount = (d) =>
  String(d?.name ?? "").trim().toLowerCase() === PRO_BONO_DISCOUNT_NAME.toLowerCase();

export const hasProBonoDiscount = (quoteDiscounts) => (quoteDiscounts || []).some(isProBonoDiscount);

export const makeProBonoDiscount = (reason) => ({
  key: `qd_probono_${Date.now()}`,
  name: PRO_BONO_DISCOUNT_NAME,
  type: "%",
  value: 100,
  reason: String(reason ?? "").trim() || PRO_BONO_DISCOUNT_NAME,
});

/**
 * The toggle. Returns the PATCH to spread onto a quote or a job:
 *   on  → billing "pro_bono", plus ONE Pro bono discount if none is named yet;
 *         a discount already named Pro bono is normalized back to 100% (GO
 *         review 2026-09-11: a hand-edited 50% line used to block the switch
 *         from delivering $0). Editing it AFTER the switch is on still shows
 *         the mismatch line rather than being repaired behind the operator.
 *   off → billing "standard", with every Pro bono discount removed
 * Idempotent: on twice adds one discount, off twice removes nothing more.
 * Other discounts are never touched. `reason` seeds the new discount's reason
 * (default "Pro bono") and is ignored when the discount already exists.
 */
export function setProBono(doc, on, { reason } = {}) {
  const existing = Array.isArray(doc?.quoteDiscounts) ? doc.quoteDiscounts : [];
  if (on) {
    const normalized = existing.map((d) => (isProBonoDiscount(d) && !(d.type === "%" && Number(d.value) === 100)
      ? { ...d, type: "%", value: 100 }
      : d));
    return {
      billing: "pro_bono",
      quoteDiscounts: hasProBonoDiscount(normalized) ? normalized : [...normalized, makeProBonoDiscount(reason)],
    };
  }
  return {
    billing: "standard",
    quoteDiscounts: existing.filter((d) => !isProBonoDiscount(d)),
  };
}

/**
 * The builder's inline warning: the toggle is on but the money does not read
 * $0, because the operator deleted or edited the Pro bono discount by hand.
 * Never re-adds the discount; the operator sees the line and decides.
 * `total` is calcTotals(...).total, passed in so this module stays import-free.
 */
export const proBonoTotalMismatch = (doc, total) => isProBono(doc) && (Number(total) || 0) > 0;

/** Client-facing copy for the warning row. No em dashes. */
export const PRO_BONO_MISMATCH_TEXT = "Pro bono is on but the total is not $0.";

/**
 * The CRM ingest signal: every contact linked to a booked pro bono job carries
 * the `pro_bono` tag. Adds it once, keeps every other tag, and returns the SAME
 * object when nothing changes so callers can skip the write.
 */
export function withProBonoTag(contact) {
  const tags = Array.isArray(contact?.tags) ? contact.tags : [];
  if (tags.some((t) => String(t).trim().toLowerCase() === PRO_BONO_TAG)) return contact;
  return { ...contact, tags: [...tags, PRO_BONO_TAG] };
}
